import { Box, Radio, Slider, ThemeProvider, Typography, styled } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import { useEffect, useMemo, useState } from "react";
import Modal from "../Modal";

import { Checkbox as MuiCheckbox } from "@mui/material";
import { useFilter } from "context/FilterContext";
import Button from "../Button";

import { useQueryClient } from "@tanstack/react-query";
import { useGlobalContext } from "context/GlobalContext";
import { usePermissions } from "context/PermissionsContext";
import { useLocation } from "react-router-dom";
import customTheme from "thems/filterModalThems";
import { findSpecificMenu, findSpecificPage, findSpecificSubMenu, hasAccessPage, isAllowed } from "utils/checkUserRigths";
import { getIcon } from "utils/iconsConfig";
import { usePermissionFlags } from "utils/visionRights";
import ChipSelect from "./ChipSelect";
import CustomSelect from "./CustomSelect";
import "./filter.css";
import renderIcon from "./renderIcon";
import { ReactComponent as BlackDateIcon } from "assets/icons/Black_Calender.svg";
import DatePickerComp from "features/Setting/order/components/react-DatePicker/DatePickerComp";

const CustomButton = styled(Button)(({}) => ({
  width: "100%",
  fontFamily: "Poppins",
  fontWeight: 500,
  fontSize: "18px",
  lineHeight: "22px",
  // height: "52px",
  borderRadius: "5px",
  color: "#00000",
  textTransform: "none",
  backgroundColor: "#50C878",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#50C878",
    boxShadow: "none",
  },
  "&:active": {
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "#444444",
    color: "#000000",
  },
}));

const StyledSlider = styled(Slider)({
  color: "#000",
  marginLeft: "35px",
  "& .MuiSlider-track": {
    backgroundColor: "#000",
    border: "none",
  },
  "& .MuiSlider-rail": {
    backgroundColor: "#D3D3D3", // 👈 grey rail
    opacity: 1,
  },
  "& .MuiSlider-thumb": {
    backgroundColor: "#000",
    "&:hover, &.Mui-focusVisible": {
      boxShadow: "0px 0px 0px 6px rgba(0,0,0,0.1)",
    },
  },
  "& .MuiSlider-valueLabel": {
    backgroundColor: "#fff",
    color: "#000",
    border: "2px solid #000",
    borderRadius: "6px",
    fontSize: "15px",
    fontWeight: 600,
    padding: "4px 9px",
    minWidth: "50px",
    maxWidth: "60px",
    top: "-20px", // 👈 more gap from thumb
    "&::before": {
      content: '""',
      position: "absolute",
      bottom: "-14px", // 👈 longer arrow
      left: "50%",
      transform: "translateX(-50%)",
      width: 0,
      height: 0,
      borderLeft: "7px solid transparent", // 👈 wider
      borderRight: "7px solid transparent", // 👈 wider
      borderTop: "14px solid #000", // 👈 taller
    },
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: "-11px",
      left: "50%",
      transform: "translateX(-50%)",
      width: 0,
      height: 0,
      borderLeft: "5px solid transparent", // 👈 wider
      borderRight: "5px solid transparent", // 👈 wider
      borderTop: "12px solid #fff", // 👈 taller
    },
  },
});

function FilterModal({ open, setOpen }) {
  const permission = usePermissions();
  const location = useLocation();
  const hasAccessToSalesHighestSeller = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Highest Seller Trophy"), "view");
  // const hasAccessToHighestSeller = hasAccessSubPage(findSpecificSubPage(findSpecificPage("Sales Count", permission), "Highest Seller Trophy"), "view");
  // const hasAccessToSeeSales = hasAccessPage(findSpecificPage("Sales Count", permission), "view");
  const hasAccessToYearlySalesData = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Sales By year"), "view");
  const hasAccessToClientSalesData = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Sales By Client"), "view");
  const hasAccessToSeeSales = hasAccessToYearlySalesData || hasAccessToClientSalesData;
  const { stockLocations } = usePermissionFlags();
  

  const initialList = {
    Pieces: [],
    Collections: [],
    Categories: [],
    Ratio: [],
    "Diamond Pieces": [],
    "Diamond Weight": [],
    "Gold Gross-Weight": [],
    "Gold Net-Weight": [],
    "Design In Collection": ["Yes", "No", "All"],
    "Design In Sets": ["Yes", "No", "All"],
    "Product Page Stock Status": ["In-Stock", "Out Of Stock", "All"],
    ...(hasAccessToSeeSales && hasAccessToSalesHighestSeller ? { "Highest Seller": ["Gold", "Silver", "Bronze"] } : {}),
    Labour: ["R", "X", "X2", "X5", "X8", "XX"],
    aiCollectionFilter: ["Ai Collection", "User-created Collection"],
    "Diamond Quality": ["VVS-EF", "VVS-FG", "VVS-GH", "VS-GH", "VSSI-GH", "SI-HI", "SI-IJ"],
    "Gold Purity": location.pathname === "/CartOrder" ? ["18K", "14K"] : ["22K", "18K", "14K"],
    "Gold Color": ["Rose Gold", "White Gold", "Yellow Gold"],
    Class: ["Gold", "Silver", "Bronze", "Under Ratio"],
    // Location: ["Headquarters Stock", "BBM Stock", "BBM Customer Stock", "BBS Stock", "BBS Customer Stock", "IDL"],
    Location: stockLocations || [],
    "Restricted Type": ["Auto - Restricted", "User - Restricted", "Both"],
    "Stock Status": ["On Hand", "On Memo", "All"],
    "Client Status": ["WIP", "Quotes & Order", "Dues", "Not Active", "Dead Since 1 year"],
    Timeline: ["Last 7 days", "Last 15 days", "Last 30 days", "Last 3 months", "Last 6 months", "Last 1 years", "Last 3 years", "All time"],
    "Service Request Type": ["Manufacturing Defects", "QC Failed", "Repairing Request"],
    Status: ["Review", "Accepted", "Received", "Production", "Need your Approval", "Ready for Dispatch", "Dispatched", "Cancelled"],
    "Number of Designs": [],
    "Created By": {},
    "From Date To Date": [],
    "Design Create Date": [],
    "Show Hidden Designs": false,
  };

  const queryClient = useQueryClient();
  const {
    filterValues,
    setFilterValues,
    backendFilterValues,
    setBackendFilterValues,
    setFilterCount,
    filterCount,
    appliedFilterValues,
    setAppliedFilterValues,
    createdByOptions,
    isCatalogFilter,
    setIsCatalogFilter,
    setFilter,
    designRange,
  } = useFilter(); // global filter
  //backendFilterValues : - Filter for storing original filterValues , this filter Values will at end points of slider
  //filterValues(setFilterValues):-  will at the actual slider moved at that position
  const [tempFilterValues, setTempFilterValues] = useState({
    piecesCount: [],
    collectionName: [],
    categoriesName: [],
  });

  const { setDynamicFilterCount } = useGlobalContext();

  const [tempFilterCount, setTempFilterCount] = useState(filterCount);
  const [flag, setFlag] = useState(false);
  const [showClrAllBtn, setShowClrAllBtn] = useState(false);
  const [showResetBtn, setShowResetBtn] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const isFilter = searchParams.get("isFilter") === "true";
  const [sliderInputValues, setSliderInputValues] = useState({});
  const [focusedKey, setFocusedKey] = useState(null);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [datePickerCategory, setDatePickerCategory] = useState("From Date To Date");
  const isDateRangeCategory = (category) => ["From Date To Date", "Design Create Date"].includes(category);

  const compareFilterChanges = (filterValues, tempFilterValues, skipKeys = []) => {
    let changeCount = 0;
    Object.keys(filterValues).forEach((key) => {
      if (skipKeys.includes(key)) return;
      if (key === "Show Hidden Designs") {
        if (filterValues[key] !== tempFilterValues[key]) {
          changeCount++;
        }
      } else if (Array.isArray(filterValues[key]) && Array.isArray(tempFilterValues[key])) {
        const original = filterValues[key];
        const current = tempFilterValues[key];

        // For 'labour', 'class', and 'highestSeller' count will inc for each checkbox
        if (
          key === "Labour" ||
          key === "Class" ||
          key === "Highest Seller" ||
          key === "Diamond Quality" ||
          key === "Gold Purity" ||
          key === "Gold Color" ||
          key === "Location" ||
          key === "Client Status" ||
          key === "Service Request Type" ||
          key === "Status" ||
          key === "aiCollectionFilter" ||
          key === "Number of Designs" ||
          key === "Created By" ||
          key === "From Date To Date"
        ) {
          // Calculate the difference in the arrays
          const removedItems = original.filter((item) => !current.includes(item)); // Items removed
          const addedItems = current.filter((item) => !original.includes(item)); // Items added

          // Increment changeCount only for the total changes in these filters
          changeCount += removedItems.length + addedItems.length;
        } else {
          if (original.length !== current.length || !original.every((val, index) => val === current[index])) {
            changeCount++;
          }
        }
      }
    });

    return changeCount;
  };

  const handleSelectValue = (event, category) => {
    let newSelectValue = event.target.value;
    setTempFilterValues((pre) => ({
      ...pre,
      [category]: category === "categoriesName" ? newSelectValue : [newSelectValue],
    }));
  };
  const handleSelectCreatedBy = (event, category) => {
    const selectedId = event.target.value;

    const selectedUser = createdByOptions?.find((user) => user.id === selectedId || user._id === selectedId);

    setTempFilterValues((prev) => ({
      ...prev,
      [category]: {
        id: selectedId,
        name: selectedUser?.username,
      },
    }));
  };
  const formatDate = (date) => {
    if (!date) return "";

    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };
  const handleCheckboxChange = (category) => (event) => {
    const { value, checked } = event.target;

    setTempFilterValues((prev) => {
      let newValues;
      // For single-select categories
      if (
        category === "Design In Sets" ||
        category === "Design In Collection" ||
        category === "Best Seller Design" ||
        category === "Restricted Type" ||
        category === "Stock Status" ||
        category === "Product Page Stock Status" ||
        category === "Timeline" ||
        ((category === "Diamond Quality" || category === "Gold Purity" || category === "Gold Color") && location.pathname === "/CartOrder")
      ) {
        newValues = checked ? [value] : [];
      } else {
        // For multi-select categories
        newValues = [...(Array.isArray(prev[category]) ? prev[category] : [])];

        if (checked) {
          newValues.push(value); // Add the selected value
        } else {
          newValues = newValues.filter((item) => item !== value); // Remove the unchecked value
        }
      }

      return {
        ...prev,
        [category]: newValues,
      };
    });
  };

  const handleShowHiddenDesignsChange = (event) => {
    setTempFilterValues((prev) => ({
      ...prev,
      ["Show Hidden Designs"]: event.target.checked,
    }));
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClearAllFilter = () => {
    setTempFilterValues(backendFilterValues);
    // setFilterCount(tempFilterCount);
    setShowClrAllBtn(false);
    setShowResetBtn(false);
  };

  const handleResetFilter = (category) => {
    setTempFilterValues((prev) => ({
      ...prev,
      [category]: backendFilterValues[category],
    }));
  };

  const handleApplyFilter = () => {
    setFilterValues(tempFilterValues);
    setAppliedFilterValues(tempFilterValues);
    setFilterCount(tempFilterCount);
    setDynamicFilterCount(true);
    handleClose();
  };

  const handleSliderChange = (category, value) => {
    setTempFilterValues((prev) => ({
      ...prev,
      [category]: value,
    }));
  };

  const hasCategoryChanged = (category) => {
    return JSON.stringify(tempFilterValues[category]) !== JSON.stringify(backendFilterValues[category]);
  };

  const CircularCheckbox = styled(MuiCheckbox)(({ theme }) => ({
    ...theme.components.MuiCheckbox.styleOverrides.root,
  }));

  const CircularRadio = styled(Radio, {
    shouldForwardProp: (prop) => prop !== "isInitialSelected",
  })(({ isInitialSelected }) => ({
    color: "#000", // Default radio ka color
    padding: "0px",
    "&.Mui-checked": {
      color: isInitialSelected ? "#E77B7B" : "#50C878",
    },
  }));

  useEffect(() => {
    setTempFilterValues((prev) => {
      const updatedValues = { ...prev };

      // Include Labour and Class values
      if (filterValues?.Labour?.length >= 0) {
        updatedValues.Labour = [...filterValues.Labour];
      } else {
        delete updatedValues["Labour"];
      }
      if (filterValues?.Class?.length >= 0) {
        updatedValues.Class = [...filterValues.Class];
      } else {
        delete updatedValues.Class;
      }
      if (filterValues["Diamond Quality"]?.length > 0) {
        updatedValues["Diamond Quality"] = [...filterValues["Diamond Quality"]];
      } else {
        delete updatedValues["Diamond Quality"];
      }
      if (filterValues["Gold Purity"]?.length > 0) {
        updatedValues["Gold Purity"] = [...filterValues["Gold Purity"]];
      } else {
        delete updatedValues["Gold Purity"];
      }
      if (filterValues["Gold Color"]?.length > 0) {
        updatedValues["Gold Color"] = [...filterValues["Gold Color"]];
      } else {
        delete updatedValues["Gold Color"];
      }
      if (filterValues?.Location?.length > 0) {
        updatedValues.Location = [...filterValues.Location];
      } else {
        delete updatedValues["Location"];
      }
      if (filterValues["Highest Seller"]?.length >= 0) {
        updatedValues["Highest Seller"] = [...filterValues["Highest Seller"]];
      } else {
        delete updatedValues["Highest Seller"];
      }
      if (filterValues["Design In Collection"]?.length >= 0) {
        updatedValues["Design In Collection"] = [...filterValues["Design In Collection"]];
      } else {
        delete updatedValues["Design In Collection"];
      }
      if (filterValues["Design In Sets"]?.length >= 0) {
        updatedValues["Design In Sets"] = [...filterValues["Design In Sets"]];
      } else {
        delete updatedValues["Design In Sets"];
      }
      if (filterValues["Stock Status"]?.length >= 0) {
        updatedValues["Stock Status"] = [filterValues["Stock Status"]?.[0]];
      } else {
        delete updatedValues["Stock Status"];
      }

      if (filterValues["Client Status"]?.length > 0) {
        updatedValues["Client Status"] = [...filterValues["Client Status"]];
      } else {
        delete updatedValues["Client Status"];
      }

      if (filterValues.Timeline?.length >= 0) {
        updatedValues.Timeline = [filterValues.Timeline?.[0]];
      } else {
        delete updatedValues.Timeline;
      }

      if (filterValues["Product Page Stock Status"]?.length >= 0) {
        updatedValues["Product Page Stock Status"] = [filterValues["Product Page Stock Status"]?.[0]];
      } else {
        delete updatedValues["Product Page Stock Status"];
      }

      if (filterValues["Best Seller Design"]?.length >= 0) {
        updatedValues["Best Seller Design"] = [...filterValues["Best Seller Design"]];
      } else {
        delete updatedValues["Best Seller Design"];
      }
      if (filterValues?.Pieces?.length > 0) {
        updatedValues.Pieces = [...filterValues.Pieces];
      } else {
        delete updatedValues["Pieces"];
      }
      if (filterValues?.Collections?.length > 0) {
        updatedValues.Collections = [...filterValues.Collections];
      } else {
        delete updatedValues["Collections"];
      }
      if (filterValues?.Categories?.length > 0) {
        updatedValues.Categories = [...filterValues.Categories];
      } else {
        delete updatedValues["Categories"];
      }
      if (filterValues?.aiCollectionFilter?.length >= 0) {
        updatedValues.aiCollectionFilter = [...filterValues.aiCollectionFilter];
      } else {
        delete updatedValues.aiCollectionFilter;
      }
      if (filterValues?.piecesCount?.length > 0) {
        updatedValues.piecesCount = [...filterValues.piecesCount];
      } else {
        // delete updatedValues["piecesCount"];
        updatedValues.piecesCount = [];
      }
      if (filterValues?.categoriesName?.length > 0) {
        updatedValues.categoriesName = [...filterValues.categoriesName];
      } else {
        updatedValues.categoriesName = [];
      }
      if (filterValues?.collectionName?.length > 0) {
        updatedValues.collectionName = [...filterValues.collectionName];
      } else {
        updatedValues.collectionName = [];
      }

      if (filterValues["Restricted Type"]?.length >= 0) {
        updatedValues["Restricted Type"] = [...filterValues["Restricted Type"]];
      } else {
        delete updatedValues["Restricted Type"];
      }
      if (Array.isArray(filterValues["Design Create Date"])) {
        updatedValues["Design Create Date"] = [...filterValues["Design Create Date"]];
      } else {
        delete updatedValues["Design Create Date"];
      }
      if (filterValues.hasOwnProperty("Show Hidden Designs")) {
        updatedValues["Show Hidden Designs"] = filterValues["Show Hidden Designs"];
      } else {
        delete updatedValues["Show Hidden Designs"];
      }
      if (filterValues["Service Request Type"]?.length > 0) {
        updatedValues["Service Request Type"] = [...filterValues["Service Request Type"]];
      }
      //  else {
      //   delete updatedValues["Service Request Type"];
      // }

      if (filterValues?.Status?.length > 0) {
        updatedValues.Status = [...filterValues.Status];
      }
      // else {
      //   delete updatedValues["Status"];
      // }
      // if (filterValues?.piecesCount) {
      //   updatedValues.piecesCount = [];
      // }
      // if (filterValues?.collectionName) {
      //   updatedValues.collectionName = [];
      // }

      // Handle slider values
      Object.keys(filterValues).forEach((key) => {
        if (["Ratio", "Diamond Pieces", "Diamond Weight", "Gold Gross-Weight", "Gold Net-Weight", "Number of Designs"].includes(key)) {
          // if (!prev[key]?.length && filterValues[key]?.length === 2) {
          //   updatedValues[key] = [...filterValues[key]]; // Set initial slider range
          // }
          if (filterValues[key]?.length === 2) {
            updatedValues[key] = [...filterValues[key]]; // Set initial slider range
          }
        }
      });

      ["Ratio", "Diamond Pieces", "Diamond Weight", "Gold Gross-Weight", "Gold Net-Weight", "Number of Designs"].forEach((key) => {
        if (!filterValues[key]) {
          delete updatedValues[key];
        }
      });

      return updatedValues;
    });
  }, [filterValues]);

  //categories chip delete karnya sathi
  function handleDeleteCategory(categoryToDelete) {
    setTempFilterValues((prev) => {
      return {
        ...prev,
        categoriesName: prev.categoriesName.flat().filter((item) => item !== categoryToDelete),
      };
    });
  }

  useEffect(() => {
    const noClrAllKeys = ["Design In Collection", "Design In Sets", "Product Page Stock Status", "Stock Status"];
    const changesForClearAll = compareFilterChanges(filterValues, tempFilterValues, noClrAllKeys);
    const changesForApply = compareFilterChanges(filterValues, tempFilterValues);
    setShowClrAllBtn(changesForClearAll >= 1);
    setFlag(changesForApply > 0);
    setTempFilterCount(changesForApply);
  }, [filterValues, tempFilterValues, backendFilterValues]);

  useEffect(() => {
    const noClrAllKeys = ["Design In Collection", "Design In Sets", "Product Page Stock Status", "Stock Status"];
    const changes = compareFilterChanges(backendFilterValues, tempFilterValues);
    const changesForClearAll = compareFilterChanges(backendFilterValues, tempFilterValues, noClrAllKeys);

    //To handle set filter count visibilty
    if (localStorage.getItem("navigatingToDesignPage") === "true" && changes > 0) {
      setFilterCount(changes);
    }
    //To show aiStock applied filter on redirecting
    if (isFilter) {
      setFilterCount(changes);
    }

    setShowClrAllBtn(changesForClearAll >= 1);
    setTempFilterCount(changes);
  }, [open, tempFilterValues]);

  useEffect(() => {
    const changes = compareFilterChanges(backendFilterValues, tempFilterValues);
    if (!(localStorage.getItem("navigatingToDesignPage") === "true") && changes === 0) {
      setFilterCount(changes);
    }
  }, [filterValues]);

  useEffect(() => {
    sessionStorage.setItem("appliedFilterValues", JSON.stringify(appliedFilterValues));
    sessionStorage.setItem("filterChangeCount", filterCount);
  }, [appliedFilterValues, filterCount]);

  useEffect(() => {
    let changes = sessionStorage.getItem("filterChangeCount");
    setFlag(changes > 0);
    if (changes > 0) {
      setFilterCount(changes);
    }
  }, [filterValues]);

  const clientStatusColors = {
    "Quotes & Order": "#C7B34E",
    Dues: "#B98D35",
    "Not Active": "#B16A63",
    WIP: "#51A47A",
    "Dead Since 1 year": "#7F7F7F",
  };

  const sliderRanges = useMemo(() => {
    return {
    Ratio: { min: backendFilterValues["Ratio"]?.[0] ?? 0, max: backendFilterValues["Ratio"]?.[1] ?? 100 },
    "Diamond Pieces": { min: backendFilterValues["Diamond Pieces"]?.[0] ?? 0, max: backendFilterValues["Diamond Pieces"]?.[1] ?? 100 },
      "Diamond Weight": { min: backendFilterValues["Diamond Weight"]?.[0] ?? 0, max: backendFilterValues["Diamond Weight"]?.[1] ?? 100 },
      "Gold Gross-Weight": { min: backendFilterValues["Gold Gross-Weight"]?.[0] ?? 0, max: backendFilterValues["Gold Gross-Weight"]?.[1] ?? 100 },
      "Gold Net-Weight": { min: backendFilterValues["Gold Net-Weight"]?.[0] ?? 0, max: backendFilterValues["Gold Net-Weight"]?.[1] ?? 100 },
      "Number of Designs": { min: backendFilterValues["Number of Designs"]?.[0] ?? 0, max: backendFilterValues["Number of Designs"]?.[1] ?? 1000 },
    };
  }, [backendFilterValues]);  

  return (
    <ThemeProvider theme={customTheme}>
      <Modal open={open} handleClose={handleClose} style={{ width: "330px", overflowX: "hidden" }}>
        <div style={{ marginRight: "10px", width: "325px" }}>
          {/* heading part */}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              gap: "2rem",
              position: "sticky",
              zIndex: 1,
              top: 0,
              backgroundColor: "white",
              height: "3rem",
              width: "100%",
            }}
          >
            <Typography className="filter-modal-heading">Filter By</Typography>
            {(!location.pathname.startsWith("/Stock") || !location.pathname === "/Clients" || location.pathname.split("/").filter(Boolean).length > 2) && showClrAllBtn && (
              <Typography className="filter-modal-subheading" sx={{ cursor: "pointer" }} onClick={handleClearAllFilter}>
                Clear All
              </Typography>
            )}
          </div>

          {/* middle part */}
          <Box
            mt={1}
            sx={{
              width: "100%",
              position: "relative",
              height: "auto",
              maxHeight: "450px",
              overflowY: "auto",
              overflowX: "hidden",
              backgroundColor: "white",
              "&::-webkit-scrollbar": {
                width: "0px",
                height: "0px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "transparent",
              },
            }}
          >
            <FormControl sx={{ width: "100%" }}>
              {Object.keys(initialList).map((category) => {
                let isKeyExit = Object.keys(filterValues)?.includes(category);
                
                if (!isKeyExit) {
                  return null;
                }
                if (isFilter) {
                  isKeyExit = ["Number of Designs", "Created By", "From Date To Date"].includes(category);
                }
                // Check if the category needs a slider
                const isSliderCategory = ["Ratio", "Diamond Pieces", "Diamond Weight", "Gold Gross-Weight", "Gold Net-Weight", "Number of Designs"].includes(category);
                const isSelectCategory = ["Pieces", "Collections", "Categories"].includes(category);

                // This two key is using for marginBottom on filter
                const isAiCollectionSelectCategory = ["aiCollectionFilter"].includes(category);
                const isLocationCategory = ["Location"].includes(category);
                if (isLocationCategory && (!initialList[category] || initialList[category].length === 0)) {
                  return null;
                }

                // Special handling for Show Hidden Designs boolean filter
                if (category === "Show Hidden Designs") {
                  return (
                    <div
                      key={category}
                      style={{
                        marginBottom: "",
                        width: "100%",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <FormLabel>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingRight: "10px" }}>
                          {category}
                          {getIcon(category)}
                        </div>
                      </FormLabel>
                      <FormControlLabel
                        control={<CircularCheckbox checked={tempFilterValues["Show Hidden Designs"] === true} onChange={handleShowHiddenDesignsChange} value="Show Hidden Designs" />}
                        label=""
                        className="filter-modal-FormControlLabel-div"
                      />
                    </div>
                  );
                }

                // const isSelectCategory = ["Pieces", "Collections"].includes(category);
                return (
                  <div
                    key={category}
                    style={{
                      marginBottom: isAiCollectionSelectCategory || isLocationCategory ? "" : "1.5rem",
                      width: !isSliderCategory && !isSelectCategory ? "100%" : "103%",
                      display: "flex",
                      flexDirection: !isSliderCategory ? "column" : "column",
                      alignItems: "flex-start",
                      justifyContent: "flex-start",
                      whiteSpace: "normal",
                      gap: !isSliderCategory ? "16px" : "14px",
                    }}
                  >
                    <div style={{ position: "relative", width: "100%" }}>
                      {category !== "aiCollectionFilter" && (
                        <FormLabel>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingRight: "10px" }}>
                            {category === "Product Page Stock Status" ? "Stock Status" : category === "Client Status" ? "Status" : category}
                            {getIcon(category)}
                          </div>
                        </FormLabel>
                      )}

                      {hasCategoryChanged(category) && isSliderCategory && (
                        <FormLabel
                          className="filter-modal-subheading"
                          sx={{ position: "absolute", right: 11, top: 0, lineHeight: "30px !important", cursor: "pointer" }}
                          onClick={() => handleResetFilter(category)}
                        >
                          Reset
                        </FormLabel>
                      )}
                    </div>

                    <div className="filter-modal-control-div" style={{ width: "100%", display: "flex", flexDirection: !isSliderCategory ? "column" : "row" }}>
                      {isSliderCategory ? (
                        <div style={{ marginTop: "40px", width: "75%" }}>
                          {/* {tempFilterValues[category] && tempFilterValues[category].length === 2 && (
                            <FormLabel sx={{ marginTop: "20px" }}>
                              {tempFilterValues[category][0]} - {tempFilterValues[category][1]}
                            </FormLabel>
                          )} */}

                          <StyledSlider
                            value={tempFilterValues[category] || [sliderRanges[category]?.min || 0, sliderRanges[category]?.max || 100]}
                            min={sliderRanges[category]?.min ?? 0}
                            max={sliderRanges[category]?.max ?? 100}
                            step={category === "Diamond Pieces" || "Number of Designs" ? 1 : 0.1}
                            onChange={(event, value) => {
                              handleSliderChange(category, value);
                            }}
                            valueLabelDisplay="on"
                            valueLabelFormat={(val, thumb) => {
                              const key = `${category}-${thumb}`;
                              const min = sliderRanges[category]?.min ?? 0;
                              const max = sliderRanges[category]?.max ?? 100;

                              return (
                                <input
                                  type="number"
                                  value={focusedKey === key ? (sliderInputValues[key] ?? val) : val}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    const min = sliderRanges[category]?.min ?? 0;
                                    const max = sliderRanges[category]?.max ?? 100;
                                    const maxDigits = Math.floor(max).toString().length;
                                    const regexPattern = new RegExp(`^\\d{0,${maxDigits}}(\\.\\d{0,1})?$`);
                                    if (raw === "") {
                                      setSliderInputValues((prev) => ({ ...prev, [key]: raw }));
                                      return;
                                    }
                                    if (regexPattern.test(raw)) {
                                      const parsed = parseFloat(raw);
                                      if (!isNaN(parsed)) {
                                        if (parsed > max) {
                                          return;
                                        }
                                        if (parsed < min && raw.length >= min.toString().length) {
                                          return;
                                        }

                                        setSliderInputValues((prev) => ({ ...prev, [key]: raw }));
                                        const clamped = Math.min(Math.max(parsed, min), max);
                                        const newVal = [...(tempFilterValues[category] || [min, max])];
                                        newVal[thumb] = clamped;
                                        handleSliderChange(category, newVal);
                                      } else if (raw === "." || raw === "-") {
                                        setSliderInputValues((prev) => ({ ...prev, [key]: raw }));
                                      }
                                    }
                                  }}
                                  onFocus={() => {
                                    setSliderInputValues((prev) => ({ ...prev, [key]: val }));
                                    setFocusedKey(key);
                                  }}
                                  onBlur={(e) => {
                                    const parsed = parseFloat(e.target.value);
                                    const clamped = isNaN(parsed) ? val : Math.min(Math.max(parsed, min), max);
                                    const newVal = [...(tempFilterValues[category] || [min, max])];
                                    newVal[thumb] = clamped;
                                    handleSliderChange(category, newVal);
                                    setSliderInputValues((prev) => ({ ...prev, [key]: clamped }));
                                    setFocusedKey(null);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => {
                                    const input = e.target;
                                    const rawValue = input.value;
                                    const parsed = parseFloat(rawValue);

                                    if (!isNaN(parsed)) {
                                      if (parsed > max && e.key !== "Backspace" && e.key !== "Delete") {
                                        e.preventDefault();
                                      }
                                      if (parsed < min && parsed !== 0 && e.key !== "Backspace" && e.key !== "Delete") {
                                        e.preventDefault();
                                      }
                                    }
                                  }}
                                  style={{
                                    width: `${Math.max(1, String(focusedKey === key ? (sliderInputValues[key] ?? val) : val).length) * 60}px`,
                                    border: "none",
                                    outline: "none",
                                    background: "transparent",
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    color: "#000",
                                    textAlign: "center",
                                    MozAppearance: "textfield",
                                    WebkitAppearance: "none",
                                  }}
                                />
                              );
                            }}
                          />
                        </div>
                      ) : category === "Created By" ? (
                        <CustomSelect
                          value={tempFilterValues[category]?.name || ""}
                          onChange={(e) => handleSelectCreatedBy(e, category)}
                          options={createdByOptions}
                          placeholder="Enter Creator Name"
                          menuStyles={{
                            maxHeight: "200px",
                            overflowY: "auto",
                            width: "fit-content",
                          }}
                          width={"95%"}
                        />
                      ) : isDateRangeCategory(category) ? (
                        <div
                          onClick={() => {
                            setDatePickerCategory(category);
                            setOpenDatePicker(true);
                          }}
                          style={{
                            fontFamily: "Karla",

                            border: "1px solid #000",
                            borderRadius: "8px",
                            outline: "none",
                            width: "87%",
                            padding: "10px 14px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            cursor: "pointer",
                            position: "relative",
                            background: "#fff",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "18px",
                              fontFamily: "Karla",
                              fontWeight: 500,
                              fontsize: "18px",
                              leadingTrim: "NONE",
                              lineHeight: "100%",
                              letterSpacing: "0%",
                              color: tempFilterValues[category]?.[0] ? "#111" : "#b5b5b5",
                            }}
                          >
                            {tempFilterValues[category]?.[0] ? `${formatDate(tempFilterValues[category]?.[0])} - ${formatDate(tempFilterValues[category]?.[1])}` : "Select Date"}
                          </span>

                          <BlackDateIcon style={{ fontSize: 17, color: "#888" }} />
                        </div>
                      ) : isSelectCategory ? (
                        <>
                          <div style={{ width: "100%" }}>
                            {category !== "Categories" && (
                              <CustomSelect
                                value={tempFilterValues[category === "Pieces" ? "piecesCount" : "collectionName"]?.[0] || ""}
                                onChange={(e) => handleSelectValue(e, category === "Pieces" ? "piecesCount" : "collectionName")}
                                options={filterValues[category]}
                                placeholder={category === "Pieces" ? "Choose Design Quantity" : "Choose Collection"}
                                menuStyles={{
                                  maxHeight: "200px",
                                  overflowY: "auto",
                                  width: "fit-content",
                                }}
                                width={"93%"}
                                sffixValue={category === "Pieces" ? "Pieces" : null}
                              />
                            )}
                          </div>
                          {category === "Categories" && (
                            <ChipSelect
                              value={tempFilterValues["categoriesName"]?.flat(Infinity)}
                              onChange={(e) => handleSelectValue(e, "categoriesName")}
                              onDelete={handleDeleteCategory}
                              options={filterValues["Categories"]}
                              placeholder={category === "Categories" ? "Choose Categories" : ""}
                              menuStyles={{
                                maxHeight: "200px",
                                overflowY: "auto",
                              }}
                              width={"93%"}
                            />
                          )}
                        </>
                      ) : (
                        // Render Checkboxes
                        (backendFilterValues[`${category}`]?.length > 0 ? backendFilterValues[`${category}`] : initialList[`${category}`])?.map((item) => {
                          const isRadio =
                            category === "Design In Sets" ||
                            category === "Design In Collection" ||
                            category === "Best Seller Design" ||
                            category === "Restricted Type" ||
                            category === "Stock Status" ||
                            category === "Product Page Stock Status" ||
                            category === "Timeline" ||
                            ((category === "Diamond Quality" || category === "Gold Purity" || category === "Gold Color") && location.pathname === "/CartOrder");

                          return (
                            <FormControlLabel
                              key={item}
                              name={item}
                              control={
                                isRadio ? (
                                  <CircularRadio
                                    checked={tempFilterValues[category]?.includes(item)}
                                    onChange={handleCheckboxChange(category)}
                                    value={item}
                                    isInitialSelected={location.pathname === "/CartOrder" && backendFilterValues[category]?.includes(item)}
                                  />
                                ) : (
                                  <CircularCheckbox checked={tempFilterValues[category]?.includes(item)} onChange={handleCheckboxChange(category)} value={item} />
                                )
                              }
                              labelPlacement="start"
                              label={
                                <Box display="flex" alignItems="center">
                                  {/* Show only icons for "Labour", otherwise show text */}

                                  {category === "Labour" ? (
                                    getIcon(item, "black")
                                  ) : (
                                    <>
                                      {renderIcon(item, category)}
                                      {category === "Client Status" && (
                                        <div
                                          style={{
                                            width: "20px",
                                            height: "20px",
                                            backgroundColor: clientStatusColors[item],
                                            marginRight: "10px",
                                            borderRadius: "1px",
                                          }}
                                        ></div>
                                      )}
                                      <Typography
                                        className={`filter-modal-items ${
                                          category === "Class" || category === "Highest Seller" || category === "Gold Color" || category === "Location" || category === "Status"
                                            ? "filter-modal-items-margin-left"
                                            : ""
                                        }`}
                                      >
                                        {item}
                                      </Typography>
                                    </>
                                  )}
                                </Box>
                              }
                              className="filter-modal-FormControlLabel-div"
                            />
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </FormControl>
          </Box>
        </div>
        {/* bottom part */}
        <Box display="flex" justifyContent="center" alignItems="end" sx={{ width: "100%", position: "sticky", bottom: 0, height: "5rem", zIndex: 10 }}>
          <CustomButton disabled={!flag} variant="contained" onClick={handleApplyFilter}>
            Apply
          </CustomButton>
        </Box>
        <DatePickerComp
          open={openDatePicker}
          handleClose={() => setOpenDatePicker(false)}
          useIn="FilterModal"
          startDate={tempFilterValues[datePickerCategory]?.[0] ? new Date(tempFilterValues[datePickerCategory][0]) : null}
          endDate={tempFilterValues[datePickerCategory]?.[1] ? new Date(tempFilterValues[datePickerCategory][1]) : null}
          onChange={(dates) => {
            const [start, end] = dates || [];
            setTempFilterValues((prev) => ({
              ...prev,
              [datePickerCategory]: [start, end],
            }));
          }}
          handleClear={() =>
            setTempFilterValues((prev) => ({
              ...prev,
              [datePickerCategory]: [],
            }))
          }
          handleApply={() => { }}
        />
      </Modal>
    </ThemeProvider>
  );
}

export default FilterModal;